/**
 * El plan de reescritura, en la pestaña Reescritura del panel de Word (13-oct-2026).
 *
 * El diagnóstico escribe el plan y el plan se quedaba en un Word: aquí se ve por fases, con una casilla por pasada
 * (desmarcar = no se hace), se aprueba y se EJECUTA fase a fase. Cada fase la hace el motor, en segundo plano y con el
 * texto GUARDADO del documento abierto, y deja al lado una versión nueva con los cambios marcados y firmados por
 * pasada; el autor decide ahí, con Revisar, guarda y sigue desde el panel de esa ventana.
 *
 * Ese recorrido se cuenta arriba, en todos los momentos (24-oct-2026), con un botón para abrir el documento con el
 * que se trabaja si no es el abierto: la novela construida, o la última versión del libro.
 *
 * Mirar y aprobar NO GASTAN. Ejecutar una fase SÍ, de la llave del autor: por eso necesita DOS clics, y entre uno y
 * otro se dice lo que viaja, lo que tarda y lo que cuesta. No habla con Word: el panel le da la carpeta y el
 * documento. Y solo pregunta sola al motor mientras hay una fase en marcha.
 */
import { useCallback, useEffect, useState } from "react";
import { COMPANION } from "./companion";
import AceptarPendientes from "./AceptarPendientes";
import { momentoDe, botonQueToca, porQueNo, FRASE_SIN_PLAN, fraseDePlanDeOtro, fraseDelOrigen, resumenDelPlan, fraseDeAprobar, seCambia, ordenDe, marcada, notaDePasada,
  fraseDeLoSiguiente, AVISO_SIN_PREFERENCIAS, rotuloDeAprobar, enMarcha, hayFaseQueLanzar, porQueNoSeLanza, hayQueAbrirLaUltima, fraseDeLoQueViaja, fraseDelTiempo, fraseDelDinero,
  rotuloDeLanzar, rotuloDeConfirmar, avanceDe, fraseDeAvance, noticiaDeFase, seRecupera, hayQueLeer, fraseDeLectura,
  PASOS_DEL_RECORRIDO, recorridoDesplegado, queAbrir, fraseDeQueAbrir, FRASE_SIN_NOVELA, ABRIR_LA_NOVELA, fraseDeNoSeAbrio,
  type Reescritura as Datos, type SinReescritura, type Orden } from "./reescrituraLogica";

export type PuenteDeLaReescritura = {
  /** El plan de este libro, por fases, y la fase que toca. No gasta ni escribe. */
  estado: (dir: string, doc: string) => Promise<Datos | SinReescritura>;
  /** Aprobar el plan, quitarle la aprobación, o desmarcar o recuperar una pasada. No gasta. */
  plan: (dir: string, doc: string, orden: Orden) => Promise<Datos | SinReescritura>;
  /** Lee lo que el autor decidió en Word con la versión abierta (la que dejó una fase). No gasta; se guarda una vez. */
  leer: (dir: string, doc: string) => Promise<Datos | SinReescritura>;
  /** Ejecuta la fase que toca. Es lo único de esta pieza que gasta. */
  fase: (dir: string, doc: string) => Promise<unknown>;
  /** Abre en Word el plan o la última versión del libro. Qué fichero es lo sabe el motor: de aquí no sale ningún nombre. */
  abrir: (dir: string, doc: string, cual: "plan" | "ultima") => Promise<{ abierto: boolean; fichero: string }>;
  /** Abre en Word lo último de la novela construida, la misma orden que «Abrirla en Word» de Corrección. Tampoco sale ningún nombre. */
  abrirLaNovela: (dir: string, doc: string) => Promise<{ abierto: boolean; fichero: string }>;
};

// El panel de Word se presenta siempre como tal.
const DE_WORD = { "x-hi-cliente": "word" };
const SIN_MOTOR = "No consigo hablar con HumanInk en este ordenador. Comprueba que está abierto.";
async function leerRespuesta(r: Response) {
  const d = await r.json().catch(() => null);
  if (r.ok && d) return d;
  // Sin licencia, el motor lo dice con su frase (`message`); lo demás, con la de cada puerta (`error`), y con su `codigo`
  // para quien tenga que decirlo de otra manera («sin_fichero» al abrir la novela).
  if (r.status === 402) throw new Error(typeof d?.message === "string" ? d.message : "Tu licencia de HumanInk no está activa en este ordenador.");
  throw Object.assign(new Error(typeof d?.error === "string" ? d.error : d?.error?.message ?? "HumanInk no ha contestado. Comprueba que está abierto en este ordenador."),
    { codigo: typeof d?.codigo === "string" ? d.codigo : undefined });
}
const alMotor = (pedido: Promise<Response>) => pedido.catch(() => { throw new Error(SIN_MOTOR); }).then(leerRespuesta);
const mandar = (ruta: string, cuerpo: object) => alMotor(fetch(`${COMPANION}${ruta}`, {
  method: "POST", headers: { "content-type": "application/json", ...DE_WORD }, body: JSON.stringify(cuerpo),
}));
export const puenteDeWord: PuenteDeLaReescritura = {
  estado: (dir, doc) => alMotor(fetch(`${COMPANION}/reescribir?${new URLSearchParams({ dir, doc })}`, { headers: DE_WORD })),
  plan: (dir, doc, orden) => mandar("/reescribir/plan", { dir, doc, ...orden }),
  leer: (dir, doc) => mandar("/reescribir/leer", { dir, doc }),
  fase: (dir, doc) => mandar("/reescribir/fase", { dir, doc, confirmar: true }),
  abrir: (dir, doc, cual) => mandar("/reescribir/abrir", { dir, doc, cual }),
  abrirLaNovela: (dir, doc) => mandar("/guion/correcciones/abrir", { dir, doc }),
};

/** Cada cuánto se pregunta por la fase EN MARCHA. Sin una en marcha no se pregunta nada. */
const CADA = 2500;

// Los estilos son de esta pieza y cuelgan de la paleta del panel (`tema.ts`): se ven en claro y en oscuro.
// `hr-principal` es el relleno, y solo lo lleva el botón del paso que toca.
export const CSS_DE_LA_REESCRITURA = `
.hr-raiz { font-size:13px; line-height:1.45; color:var(--hi-tinta); background:var(--hi-papel); border:1px solid var(--hi-raya); border-radius:10px; padding:12px; margin:4px 0 14px; }
.hr-raiz p { margin:0 0 6px; } .hr-raiz p:last-child { margin-bottom:0; }
.hr-rotulo { font-size:11px; font-weight:800; letter-spacing:.06em; text-transform:uppercase; color:var(--hi-oficio); margin-bottom:4px; }
.hr-tenue { color:var(--hi-tenue); font-size:12px; overflow-wrap:anywhere; }
.hr-botones { display:flex; flex-wrap:wrap; gap:6px; margin-top:10px; }
.hr-boton { font:inherit; font-size:13px; font-weight:700; padding:9px 12px; border-radius:8px; border:1px solid var(--hi-humano); background:transparent; color:var(--hi-humano); cursor:pointer; }
.hr-boton:focus-visible { outline:2px solid var(--hi-humano); outline-offset:2px; }
.hr-boton:disabled { opacity:.5; cursor:not-allowed; }
.hr-principal { background:var(--hi-oficio); border-color:var(--hi-oficio); color:var(--hi-sobre-acento); font-weight:800; }
.hr-nota { border:1px solid var(--hi-aviso-raya); border-left-width:4px; border-radius:8px; padding:10px 12px; margin-top:8px; overflow-wrap:anywhere; }
.hr-nota[data-tono="bien"] { border-color:var(--hi-bien-raya); }
.hr-nota[data-tono="mal"] { border-color:var(--hi-mal-raya); }
.hr-avance { width:100%; height:8px; margin:2px 0 6px; accent-color:var(--hi-oficio); }
.hr-cuenta { border-top:1px solid var(--hi-raya); margin-top:10px; padding-top:10px; }
.hr-lista { margin:6px 0; padding-left:20px; } .hr-lista li { margin-bottom:2px; }
.hr-fases { list-style:none; margin:10px 0 0; padding:0; display:grid; gap:10px; }
.hr-fase { border-top:1px solid var(--hi-raya); padding-top:10px; }
.hr-fase[data-toca="si"] > .hr-fase-nombre { color:var(--hi-oficio); }
.hr-fase-nombre { font-size:13px; font-weight:800; margin:0 0 6px; text-wrap:balance; }
.hr-pasadas { list-style:none; margin:0; padding:0; display:grid; gap:8px; }
.hr-pasada { display:grid; grid-template-columns:auto 1fr; gap:2px 8px; align-items:start; }
.hr-pasada input { margin:3px 0 0; width:16px; height:16px; accent-color:var(--hi-oficio); }
.hr-pasada input:disabled { cursor:not-allowed; }
.hr-pasada label { font-weight:700; overflow-wrap:anywhere; cursor:pointer; }
.hr-pasada[data-estado="saltada"] label { font-weight:400; color:var(--hi-tenue); text-decoration:line-through; }
.hr-pasada[data-estado="hecha"] label { color:var(--hi-suave); }
.hr-pasada .hr-tenue { grid-column:2; }
.hr-pasada[data-apartada="si"] label { color:var(--hi-tenue); }
.hr-enlace { grid-column:2; justify-self:start; font:inherit; font-size:12px; font-weight:700; padding:4px 10px; margin-top:2px; border-radius:8px; border:1px solid var(--hi-humano); background:transparent; color:var(--hi-humano); cursor:pointer; }
.hr-enlace:focus-visible { outline:2px solid var(--hi-humano); outline-offset:2px; }
.hr-enlace:disabled { opacity:.5; cursor:not-allowed; }
.hr-plegable { font:inherit; font-size:12px; font-weight:700; padding:2px 0; margin:0 0 4px; border:none; background:none; color:var(--hi-humano); cursor:pointer; text-align:left; }
.hr-plegable:focus-visible { outline:2px solid var(--hi-humano); outline-offset:2px; }
.hr-recorrido { margin:0 0 8px; } .hr-recorrido li { margin-bottom:4px; }
.hr-abrir { margin:0 0 10px; } .hr-abrir [role="alert"] { margin-top:8px; }
`;

const SIN_GUARDAR = "Guarda el documento en este ordenador y vuelve a esta pestaña: el plan de reescritura es de un documento guardado.";

export default function Reescritura({ dir, doc, puente = puenteDeWord }: { dir: string; doc: string; puente?: PuenteDeLaReescritura }) {
  const [datos, setDatos] = useState<Datos | SinReescritura | null>(null);
  const [fallo, setFallo] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  /** El primer clic de la fase está dado: se enseña lo que viaja y lo que cuesta, y se espera el «sí». */
  const [pedido, setPedido] = useState(false);
  /** Cada vez que se termina de mirar, salga bien o mal: con una fase en marcha, es lo que vuelve a armar el reloj. */
  const [vuelta, setVuelta] = useState(0);
  /** El recorrido, desplegado o plegado por el autor. `null`: lo que toque por el libro (`recorridoDesplegado`). */
  const [recorrido, setRecorrido] = useState<boolean | null>(null);
  /** Lo que ha contestado el botón de arriba, el de abrir el documento con el que se trabaja: sale junto a él. */
  const [alAbrir, setAlAbrir] = useState<string | null>(null);

  const mirar = useCallback(async () => {
    if (!dir || !doc) return;
    try { setDatos(await puente.estado(dir, doc)); setFallo(null); }
    catch (e) { setFallo(e instanceof Error ? e.message : String(e)); }
    finally { setVuelta((v) => v + 1); }
  }, [dir, doc, puente]);

  useEffect(() => {
    void mirar();
    const alVolver = () => void mirar();
    window.addEventListener("focus", alVolver);
    return () => window.removeEventListener("focus", alVolver);
  }, [mirar]);

  // Solo mientras hay una fase en marcha: se pregunta cómo va, y se deja de preguntar en cuanto acaba.
  const trabajando = !!datos && datos.hay && enMarcha(datos.trabajo);
  useEffect(() => {
    if (!trabajando) return;
    const reloj = setTimeout(() => void mirar(), CADA);
    return () => clearTimeout(reloj);
  }, [trabajando, vuelta, mirar]);

  const hacer = async (que: () => Promise<unknown>) => {
    if (ocupado) return;
    setOcupado(true); setFallo(null);
    try { await que(); }
    // Si algo falla se mira otra vez antes de decirlo: lo que haya cambiado tiene que verse ya.
    catch (e) { await mirar(); setFallo(e instanceof Error ? e.message : String(e)); }
    finally { setOcupado(false); setPedido(false); }
  };
  /** Aprobar, quitar la aprobación, desmarcar o recuperar: el motor contesta con el plan como queda. */
  const ordenar = (orden: Orden) => void hacer(async () => { setDatos(await puente.plan(dir, doc, orden)); });
  const abrir = (cual: "plan" | "ultima") => void hacer(async () => {
    const r = await puente.abrir(dir, doc, cual);
    // La última, con la misma frase que el botón de arriba: los dos «Abrir la última versión» contestan igual.
    if (!r.abierto) throw new Error(cual === "ultima" ? fraseDeNoSeAbrio(r.fichero) : `No he podido abrir «${r.fichero}». Está en la carpeta del libro, junto a tu manuscrito.`);
  });
  /**
   * El documento con el que se trabaja, si no es el abierto (`queAbrir`): la novela construida o la última versión.
   * Lo que conteste se dice junto al botón, que es donde está mirando el autor, y no en el fallo de abajo.
   */
  const abrirElDeTrabajo = (cual: "novela" | "ultima") => void hacer(async () => {
    setAlAbrir(null);
    try {
      const r = cual === "novela" ? await puente.abrirLaNovela(dir, doc) : await puente.abrir(dir, doc, "ultima");
      if (!r.abierto) setAlAbrir(fraseDeNoSeAbrio(r.fichero));
    } catch (e) {
      setAlAbrir(cual === "novela" && (e as { codigo?: string }).codigo === "sin_fichero" ? FRASE_SIN_NOVELA : e instanceof Error ? e.message : String(e));
    }
  });
  /** «Leer lo que decidí»: el motor lee la versión abierta, lo apunta y contesta con el plan como queda. */
  const leerLoDecidido = () => void hacer(async () => { setDatos(await puente.leer(dir, doc)); });
  /** El PRIMER clic de la fase: no pide nada. Enseña la cuenta (y mira otra vez, por si has guardado desde que se abrió la pestaña). */
  const verLaCuenta = () => { setPedido(true); void mirar(); };
  /** El SEGUNDO clic: aquí, y solo aquí, se ejecuta la fase. */
  const confirmar = () => void hacer(async () => { await puente.fase(dir, doc); await mirar(); });

  // EL RECORRIDO (24-oct-2026), en todos los momentos y antes que el plan: lo que se descubre al final de la pestaña es
  // como si no existiera. A la vista hasta la primera pasada hecha; después, plegado y a un clic.
  const desplegado = recorrido ?? (datos ? recorridoDesplegado(datos) : !doc);
  // Y, aunque esté plegado, el documento con el que se trabaja si no es el abierto: su frase y su botón.
  const cualAbrir = datos ? queAbrir(datos) : null;
  const marco = (dentro: React.ReactNode) => (
    <section className="hr-raiz" aria-label="El plan de reescritura">
      <style>{CSS_DE_LA_REESCRITURA}</style>
      <div className="hr-rotulo">El plan de reescritura</div>
      <button className="hr-plegable" aria-expanded={desplegado} onClick={() => setRecorrido(!desplegado)}>Cómo se reescribe, paso a paso {desplegado ? "▲" : "▼"}</button>
      {desplegado && <ol className="hr-lista hr-recorrido">{PASOS_DEL_RECORRIDO.map((x, i) => <li key={i}>{x}</li>)}</ol>}
      {datos && cualAbrir && (
        <div className="hr-nota hr-abrir" role="status">
          <p>{fraseDeQueAbrir(datos)}</p>
          <div className="hr-botones">
            <button className="hr-boton" disabled={ocupado} onClick={() => abrirElDeTrabajo(cualAbrir)}>{cualAbrir === "novela" ? ABRIR_LA_NOVELA : "Abrir la última versión"}</button>
          </div>
          {alAbrir && <p role="alert">{alAbrir}</p>}
        </div>
      )}
      {dentro}
    </section>
  );
  const elFallo = fallo && <div className="hr-nota" data-tono="mal" role="alert"><p>{fallo}</p></div>;
  const volverAMirar = <div className="hr-botones"><button className="hr-boton" disabled={ocupado} onClick={() => void mirar()}>Volver a mirar</button></div>;
  if (!doc) return marco(<p>{SIN_GUARDAR}</p>);
  if (!datos) return marco(elFallo || <p className="hr-tenue">Mirando el documento…</p>);

  const momento = momentoDe(datos);
  // Con el manuscrito por escenas, el porqué ya va arriba, con el botón de abrir la novela: no se dice dos veces.
  if (!datos.hay) return marco(<>{cualAbrir !== "novela" && <p>{porQueNo(datos)}</p>}{elFallo}{volverAMirar}</>);
  const d = datos, p = d.plan;
  if (!p) return marco(<><p>{momento === "de_otro" ? fraseDePlanDeOtro(d) : FRASE_SIN_PLAN}</p>{elFallo}{volverAMirar}</>);

  const toca = botonQueToca(momento, d, pedido);
  const relleno = (cual: "aprobar" | "lanzar" | "confirmar" | "abrir_ultima" | "leer") => `hr-boton${toca === cual ? " hr-principal" : ""}`;
  const t = d.trabajo, corriendo = enMarcha(t);
  const noticia = noticiaDeFase(d);
  const freno = hayFaseQueLanzar(d) && !corriendo ? porQueNoSeLanza(d) : null;
  const c = d.cuenta ?? null;
  // LA VERSIÓN ABIERTA, si la dejó una fase: lo que falta por decidir, el botón de leer lo decidido, o lo que salió.
  const laLectura = d.lectura && !corriendo && fraseDeLectura(d) && (
    <div className="hr-nota" data-tono={d.lectura.leida ? "bien" : undefined} role="status">
      <p>{fraseDeLectura(d)}</p>
      {hayQueLeer(d) && <div className="hr-botones"><button className={relleno("leer")} disabled={ocupado} onClick={leerLoDecidido}>Leer lo que decidí</button></div>}
    </div>
  );
  // LO QUE QUEDA SIN DECIDIR EN ESTE WORD (14-oct-2026): darlo por aceptado con un clic. Los cambios que cuenta el motor
  // son los del documento abierto, que es el que acepta Word. Va junto a lo que lo dice: la fase, si hay una que lanzar;
  // si no, la lectura de la versión.
  const aceptarPendientes = !corriendo && (d.marcas ?? 0) > 0 && <AceptarPendientes n={d.marcas ?? 0} dir={dir} doc={doc} alTerminar={() => void mirar()} />;
  // LA FASE QUE TOCA: en marcha, con su avance; si no, por qué no se puede lanzar, o el botón y —dado el primer clic— su cuenta.
  const laFase = hayFaseQueLanzar(d) && (
    corriendo && t ? (
      <div className="hr-nota" role="status">
        <p><b>{fraseDeAvance(t)}</b></p>
        <progress className="hr-avance" value={avanceDe(t)} max={1} aria-label="Avance de la fase" />
        <p>Puedes cerrar el panel: sigue trabajando. No cambies el texto de este documento hasta que termine.</p>
      </div>
    ) : freno ? (<>
      <div className="hr-nota"><p>{freno}</p></div>
      <div className="hr-botones">
        {hayQueAbrirLaUltima(d) && <button className={relleno("abrir_ultima")} disabled={ocupado} onClick={() => abrir("ultima")}>Abrir la última versión</button>}
        <button className="hr-boton" disabled={ocupado} onClick={() => void mirar()}>Volver a mirar</button>
      </div>
    </>) : !pedido ? (
      <div className="hr-botones"><button className={relleno("lanzar")} disabled={ocupado} onClick={verLaCuenta}>{rotuloDeLanzar(d)}</button></div>
    ) : (
      <div className="hr-cuenta">
        <p><b>{rotuloDeLanzar(d)}</b></p>
        {Array.isArray(d.fase?.pasadas) && <ol className="hr-lista">{d.fase.pasadas.map((x) => <li key={x.n}>{x.titulo}</li>)}</ol>}
        <p>Se hace con «{d.fichero}» tal como está guardado, y deja al lado una versión nueva con los cambios marcados. Este documento no se toca.</p>
        {c ? (<>
          <p>{fraseDeLoQueViaja(c)}</p>
          {fraseDelTiempo(c) && <p>{fraseDelTiempo(c)}</p>}
          {fraseDelDinero(c) && <p>{fraseDelDinero(c)}</p>}
        </>) : <p>No he podido hacer la cuenta de lo que costará.</p>}
        <div className="hr-botones">
          <button className={relleno("confirmar")} disabled={ocupado} onClick={confirmar}>{rotuloDeConfirmar(c)}</button>
          <button className="hr-boton" disabled={ocupado} onClick={() => setPedido(false)}>No, ahora no</button>
        </div>
      </div>
    )
  );
  return marco(<>
    <p><b>{resumenDelPlan(p)}</b></p>
    {fraseDelOrigen(d) && <p className="hr-tenue">{fraseDelOrigen(d)}</p>}
    <p>{fraseDeAprobar(d)}</p>
    {d.sin_preferencias && !hayFaseQueLanzar(d) && <div className="hr-nota" data-tono="mal" role="alert"><p>{AVISO_SIN_PREFERENCIAS}</p></div>}
    {d.ocupado && !hayFaseQueLanzar(d) && <div className="hr-nota" role="status"><p>{d.ocupado}</p></div>}
    {noticia && <div className="hr-nota" data-tono={noticia.tono} role={noticia.tono === "mal" ? "alert" : "status"}>{noticia.lineas.map((l, i) => <p key={i}>{l}</p>)}</div>}
    {laLectura}
    {!hayFaseQueLanzar(d) && aceptarPendientes}
    {elFallo}

    <ol className="hr-fases">
      {p.fases.map((f) => (
        <li key={f.indice} className="hr-fase" data-toca={d.fase?.fase != null && d.fase.indice === f.indice ? "si" : "no"}>
          <h3 className="hr-fase-nombre">Fase {f.indice} · {f.nombre}</h3>
          <ul className="hr-pasadas">
            {f.pasadas.map((x) => {
              const id = `hr-pasada-${x.n}`, nota = notaDePasada(x);
              return (
                <li key={x.n} className="hr-pasada" data-estado={x.estado} data-apartada={x.rechazada || x.perdida ? "si" : undefined}>
                  <input id={id} type="checkbox" checked={marcada(x)} disabled={ocupado || corriendo || !seCambia(x, d)} onChange={() => ordenar(ordenDe(x))} />
                  <label htmlFor={id}>{x.titulo}</label>
                  {x.porque && x.estado !== "saltada" && <span className="hr-tenue">{x.porque}</span>}
                  {nota && <span className="hr-tenue">{nota}</span>}
                  {(x.rechazada || x.perdida) && <button className="hr-enlace" disabled={ocupado || !seRecupera(x, d)} onClick={() => ordenar({ accion: "rehacer", n: x.n })}>Recuperar</button>}
                </li>
              );
            })}
          </ul>
        </li>
      ))}
    </ol>

    {fraseDeLoSiguiente(d) && !corriendo && <p className="hr-tenue" style={{ marginTop: 10 }}>{fraseDeLoSiguiente(d)}</p>}
    {laFase}
    {hayFaseQueLanzar(d) && aceptarPendientes}
    <div className="hr-botones">
      {momento !== "terminado" && (
        <button className={relleno("aprobar")} disabled={ocupado || corriendo || !!d.ocupado} onClick={() => ordenar({ accion: p.aprobado ? "desaprobar" : "aprobar" })}>{rotuloDeAprobar(d)}</button>
      )}
      {p.documento && <button className="hr-boton" disabled={ocupado} onClick={() => abrir("plan")}>Abrir el plan en Word</button>}
    </div>
  </>);
}
